import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './useAdminApi';

/* ------------------------------------------------------------------ */
/* Order Desk — staff-forwarded email order intake                      */
/* ------------------------------------------------------------------ */

export type OrderDeskIntent = 'order' | 'service' | 'both' | 'question' | 'other';

export interface OrderDeskMatchCandidate {
  catalog_item_id: string;
  label: string;
  sku: string | null;
  category: string | null;
  order_count: number;
}

export interface OrderDeskMatchedLine {
  line_number: number;
  description: string;
  qty: number;
  unit_hint: string | null;
  catalog_item_id: string | null;
  matched_label: string | null;
  matched_sku: string | null;
  matched_category: string | null;
  method: 'history' | 'sku' | 'search' | null;
  candidates: OrderDeskMatchCandidate[];
}

export interface OrderDeskParsed {
  intent: OrderDeskIntent;
  customer_hint: string | null;
  contact: { name: string | null; email: string | null; phone: string | null };
  lines: Array<{ description: string; qty: number; unit_hint: string | null }>;
  service: { kind: string; subject: string; body: string | null } | null;
  requested_delivery_date: string | null;
  notes: string | null;
  staff_instruction: string | null;
}

export interface OrderDeskRow {
  id: string;
  from_email: string | null;
  forwarded_from: string | null;
  subject: string | null;
  received_at: string | null;
  intent: OrderDeskIntent | null;
  status: string;
  hold_reason: string | null;
  customer_id: string | null;
  customer_name: string | null;
  identity_confidence: 'high' | 'medium' | 'low' | 'none' | null;
  identity_note: string | null;
  order_id: string | null;
  service_request_id: string | null;
  requested_delivery_date: string | null;
  proposal_sent_at: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  error: string | null;
  created_at: string;
  line_count: number;
  unmatched_count: number;
}

export interface OrderDeskDetail {
  id: string;
  from_email: string | null;
  forwarded_from: string | null;
  subject: string | null;
  received_at: string | null;
  raw_text: string | null;
  staff_note: string | null;
  quoted_text: string | null;
  attachments: Array<{ path: string; filename: string; media_type: string }> | null;
  intent: OrderDeskIntent | null;
  parsed: OrderDeskParsed | null;
  customer_id: string | null;
  customer_location_id: string | null;
  identity_source: string | null;
  identity_confidence: 'high' | 'medium' | 'low' | 'none' | null;
  identity_note: string | null;
  matched: OrderDeskMatchedLine[] | null;
  requested_delivery_date: string | null;
  status: string;
  hold_reason: string | null;
  order_id: string | null;
  service_request_id: string | null;
  error: string | null;
  proposal_sent_at: string | null;
  proposal_recipients: string[] | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface OrderDeskLocationOption {
  id: string;
  display_name: string | null;
  location_code: string | null;
  line1: string | null;
  city: string | null;
}

export const STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  processing: 'Reading…',
  needs_review: 'Needs a decision',
  submitted: 'Sent',
  service_filed: 'Service call filed',
  discarded: 'Not an order',
  failed: 'Failed',
  ignored: 'Ignored',
};

export const HOLD_REASON_LABELS: Record<string, string> = {
  no_identity: "Couldn't identify the account",
  line_match: 'Some lines need an item picked',
  no_lines: 'No order lines found',
  extract_failed: "Couldn't read the email",
  sender_rejected: 'Sender is not staff',
  submit_failed: 'Submit failed — retry',
  awaiting_accept: 'Ready — awaiting accept',
  failed: 'Failed',
};

export const INTENT_LABELS: Record<OrderDeskIntent, string> = {
  order: 'Order',
  service: 'Service call',
  both: 'Order + service',
  question: 'Question',
  other: 'Not orderable',
};

/* -------------------- queries -------------------- */

export function useOrderDeskList(opts: { status?: string } = {}) {
  const status = opts.status ?? 'needs_review';
  return useQuery({
    queryKey: ['admin', 'order-desk', 'list', status],
    queryFn: () =>
      adminFetch<{
        ok: true;
        rows: OrderDeskRow[];
        enabled: boolean;
        inbound_address: string | null;
      }>(`admin-order-desk?status=${encodeURIComponent(status)}`),
    staleTime: 15_000,
  });
}

export function useOrderDeskDetail(intakeId: string | undefined) {
  return useQuery({
    enabled: !!intakeId,
    queryKey: ['admin', 'order-desk', 'detail', intakeId],
    queryFn: () =>
      adminFetch<{
        ok: true;
        intake: OrderDeskDetail;
        customer_name: string | null;
        locations: OrderDeskLocationOption[];
        attachments: Array<{ path: string; filename: string; media_type: string; url: string | null }>;
        schedule: { date: string | null; note: string | null };
      }>(`admin-order-desk?id=${encodeURIComponent(intakeId!)}`),
    staleTime: 10_000,
  });
}

export function useOrderDeskCatalogSearch(term: string) {
  return useQuery({
    enabled: term.trim().length >= 2,
    queryKey: ['admin', 'order-desk', 'catalog', term.trim()],
    queryFn: () =>
      adminFetch<{
        ok: true;
        items: Array<{
          catalog_item_id: string;
          label: string;
          sku: string | null;
          category: string | null;
          unit: string | null;
          list_price: number | null;
        }>;
      }>(`admin-order-desk?catalog_search=${encodeURIComponent(term.trim())}`),
    staleTime: 60_000,
  });
}

export function useOrderDeskCustomerSearch(term: string) {
  return useQuery({
    enabled: term.trim().length >= 2,
    queryKey: ['admin', 'order-desk', 'customers', term.trim()],
    queryFn: () =>
      adminFetch<{
        ok: true;
        customers: Array<{ id: string; name: string; qbo_customer_id: number | null }>;
      }>(`admin-order-desk?customer_search=${encodeURIComponent(term.trim())}`),
    staleTime: 60_000,
  });
}

/* -------------------- mutations -------------------- */

export interface OrderDeskSubmitInput {
  intake_id: string;
  customer_id?: string | null;
  customer_location_id?: string | null;
  lines?: Array<{ catalog_item_id: string; qty: number; description?: string | null }>;
  requested_delivery_date?: string | null;
  notes?: string | null;
}

function useOrderDeskInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'order-desk'] });
  };
}

export function useOrderDeskSubmit() {
  const invalidate = useOrderDeskInvalidate();
  return useMutation({
    mutationFn: (input: OrderDeskSubmitInput) =>
      adminFetch<{
        ok: true;
        order_id: string | null;
        order_number: string | null;
        sf_job_id: string | null;
        service_request_id: string | null;
        customer_name: string | null;
      }>('admin-order-desk', {
        method: 'POST',
        body: JSON.stringify({ action: 'submit', ...input }),
      }),
    onSuccess: invalidate,
  });
}

export function useOrderDeskAction(action: 'discard' | 'reopen' | 'reprocess') {
  const invalidate = useOrderDeskInvalidate();
  return useMutation({
    mutationFn: (intakeId: string) =>
      adminFetch<{ ok: true }>('admin-order-desk', {
        method: 'POST',
        body: JSON.stringify({ action, intake_id: intakeId }),
      }),
    onSuccess: invalidate,
  });
}
